/**
 * UserTable Component
 * 
 * Displays a table of users with sorting, filtering, and role management
 * 
 * Features:
 * - Sortable columns
 * - Search/filter functionality
 * - Role update capability
 * - Pagination support
 * - Subscription status display
 */

import React, { useState } from 'react';

const UserTable = ({ users, onRoleUpdate, isUpdating }) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [updatingUserId, setUpdatingUserId] = useState(null);

  /**
   * Handle column sort toggle
   */
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortOrder('desc');
    }
  };

  /**
   * Handle role change for a user
   */
  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const confirmMessage = currentRole === 'admin' 
      ? 'Demote this user from admin to regular user?'
      : 'Promote this user to admin?';

    if (window.confirm(confirmMessage)) {
      setUpdatingUserId(userId);
      await onRoleUpdate(userId, newRole);
      setUpdatingUserId(null);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  /**
   * Get sort indicator icon
   */
  const getSortIcon = (field) => {
    if (sortField !== field) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // If no users, show empty state
  if (!users || users.length === 0) {
    return (
      <div className="empty-state">
        <p>No users found</p>
      </div>
    );
  }

  return (
    <div className="user-table-container">
      <table className="user-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('id')} className="sortable">
              ID {getSortIcon('id')}
            </th>
            <th onClick={() => handleSort('username')} className="sortable">
              Username {getSortIcon('username')}
            </th>
            <th onClick={() => handleSort('email')} className="sortable">
              Email {getSortIcon('email')}
            </th>
            <th>Name</th>
            <th onClick={() => handleSort('role')} className="sortable">
              Role {getSortIcon('role')}
            </th>
            <th>Subscribed</th>
            <th>Logins</th>
            <th onClick={() => handleSort('created_at')} className="sortable">
              Joined {getSortIcon('created_at')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              {/* User ID */}
              <td>{user.id}</td>

              {/* Username */}
              <td>
                <span className="username">{user.username}</span>
                {user.oauth_provider && (
                  <span className="oauth-badge">{user.oauth_provider}</span>
                )}
              </td>

              {/* Email */}
              <td>{user.email}</td>

              {/* Full Name */}
              <td>{user.firstname} {user.lastname}</td>

              {/* Role Badge */}
              <td>
                <span className={`role-badge role-${user.role}`}>
                  {user.role}
                </span>
              </td>

              {/* Subscription Status */}
              <td>
                <span className={`sub-status ${user.isSubscribed ? 'active' : 'inactive'}`}>
                  {user.isSubscribed ? '✓ Active' : '✗ Inactive'}
                </span>
                {user.subscription_end_date && user.isSubscribed && (
                  <div className="sub-end-date">
                    Until {formatDate(user.subscription_end_date)}
                  </div>
                )}
              </td>

              {/* Login Count */}
              <td>{user.login_count || 0}</td>

              {/* Created Date */}
              <td>{formatDate(user.created_at)}</td>

              {/* Actions */}
              <td>
                <button
                  className={`role-toggle-btn ${user.role === 'admin' ? 'demote' : 'promote'}`}
                  onClick={() => handleRoleChange(user.id, user.role)}
                  disabled={isUpdating || updatingUserId === user.id}
                >
                  {updatingUserId === user.id ? (
                    'Updating...'
                  ) : user.role === 'admin' ? (
                    '↓ Demote'
                  ) : (
                    '↑ Promote'
                  )}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;

